import { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fmtD } from './planningUtils';

export function PlanningSettingsDialog({ open, onOpenChange, onSaved }) {
  const [lead, setLead] = useState(2);
  const [buffer, setBuffer] = useState(1);
  const [capSettings, setCapSettings] = useState([]);
  const [newCap, setNewCap] = useState({ effective_date: '', windows_per_day: '' });
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: '', kind: 'holiday', capacity_override: '', note: '' });

  const load = useCallback(async () => {
    try {
      const [s, c, h] = await Promise.all([
        api.get('/api/admin/production-planning/settings'),
        api.get('/api/admin/production-capacity'),
        api.get('/api/admin/production-planning/holidays'),
      ]);
      setLead(s.data.min_lead_days); setBuffer(s.data.dispatch_buffer_days);
      setCapSettings(c.data.settings || []);
      setHolidays(h.data.holidays || []);
    } catch (e) { toast.error('Failed to load settings'); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const saveSettings = async () => {
    try {
      await api.put('/api/admin/production-planning/settings', { min_lead_days: Number(lead), dispatch_buffer_days: Number(buffer) });
      toast.success('Planning settings saved');
      onSaved && onSaved();
    } catch (e) { toast.error('Save failed'); }
  };

  const addCapacity = async () => {
    if (!newCap.effective_date || !newCap.windows_per_day) return toast.error('Date and capacity required');
    try {
      await api.post('/api/admin/production-capacity', { effective_date: newCap.effective_date, windows_per_day: Number(newCap.windows_per_day) });
      toast.success('Capacity setting added');
      setNewCap({ effective_date: '', windows_per_day: '' });
      load(); onSaved && onSaved();
    } catch (e) { toast.error('Failed to add capacity'); }
  };

  const delCapacity = async (id) => {
    try { await api.delete(`/api/admin/production-capacity/${id}`); load(); onSaved && onSaved(); } catch (e) { toast.error('Delete failed'); }
  };

  const addHoliday = async () => {
    if (!newHoliday.date) return toast.error('Date required');
    try {
      await api.post('/api/admin/production-planning/holidays', {
        date: newHoliday.date, kind: newHoliday.kind,
        capacity_override: newHoliday.capacity_override === '' ? null : Number(newHoliday.capacity_override),
        note: newHoliday.note,
      });
      toast.success('Saved');
      setNewHoliday({ date: '', kind: 'holiday', capacity_override: '', note: '' });
      load(); onSaved && onSaved();
    } catch (e) { toast.error('Failed to save holiday'); }
  };

  const delHoliday = async (d) => {
    try { await api.delete(`/api/admin/production-planning/holidays/${d}`); load(); onSaved && onSaved(); } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto" data-testid="planning-settings-dialog">
        <DialogHeader><DialogTitle>Production Planning Configuration</DialogTitle></DialogHeader>
        <div className="space-y-5 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-bold">Scheduling Rules</div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs">Min lead days after JO date</Label>
                <Input type="number" min="0" value={lead} onChange={(e) => setLead(e.target.value)} data-testid="setting-lead-days" />
              </div>
              <div>
                <Label className="text-xs">Dispatch buffer before delivery</Label>
                <Input type="number" min="0" value={buffer} onChange={(e) => setBuffer(e.target.value)} data-testid="setting-buffer-days" />
              </div>
              <Button onClick={saveSettings} data-testid="save-planning-settings">Save Rules</Button>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-bold">Daily Capacity (effective-from history)</div>
            <div className="grid grid-cols-3 gap-3 items-end mb-2">
              <div>
                <Label className="text-xs">Effective From</Label>
                <Input type="date" value={newCap.effective_date} onChange={(e) => setNewCap({ ...newCap, effective_date: e.target.value })} data-testid="capacity-effective-date" />
              </div>
              <div>
                <Label className="text-xs">Windows / Day</Label>
                <Input type="number" min="1" value={newCap.windows_per_day} onChange={(e) => setNewCap({ ...newCap, windows_per_day: e.target.value })} data-testid="capacity-windows" />
              </div>
              <Button onClick={addCapacity} data-testid="add-capacity-btn">Add Capacity</Button>
            </div>
            <table className="min-w-full text-xs border border-zinc-200">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr><th className="p-1.5 text-left">Effective From</th><th className="p-1.5 text-right">Windows/Day</th><th className="p-1.5 text-left">By</th><th className="p-1.5"></th></tr>
              </thead>
              <tbody>
                {capSettings.map((c) => (
                  <tr key={c._id} className="border-t border-zinc-100">
                    <td className="p-1.5">{fmtD(c.effective_date)}</td>
                    <td className="p-1.5 text-right font-bold">{c.windows_per_day}</td>
                    <td className="p-1.5 text-zinc-500">{c.created_by}</td>
                    <td className="p-1.5 text-right"><button onClick={() => delCapacity(c._id)} className="text-red-500 hover:text-red-700"><Trash size={14} /></button></td>
                  </tr>
                ))}
                {capSettings.length === 0 && <tr><td colSpan="4" className="p-3 text-center text-zinc-400">No capacity settings — default 20/day</td></tr>}
              </tbody>
            </table>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-bold">Holidays / Special Days</div>
            <div className="grid grid-cols-5 gap-2 items-end mb-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} data-testid="holiday-date" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={newHoliday.kind} onValueChange={(v) => setNewHoliday({ ...newHoliday, kind: v })}>
                  <SelectTrigger data-testid="holiday-kind"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="holiday">Holiday (0 capacity)</SelectItem>
                    <SelectItem value="half_day">Half Day</SelectItem>
                    <SelectItem value="overtime">Overtime (Sunday work)</SelectItem>
                    <SelectItem value="special">Special Capacity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Capacity Override</Label>
                <Input type="number" placeholder="auto" value={newHoliday.capacity_override} onChange={(e) => setNewHoliday({ ...newHoliday, capacity_override: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input value={newHoliday.note} onChange={(e) => setNewHoliday({ ...newHoliday, note: e.target.value })} />
              </div>
              <Button onClick={addHoliday} data-testid="add-holiday-btn">Add</Button>
            </div>
            <table className="min-w-full text-xs border border-zinc-200">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr><th className="p-1.5 text-left">Date</th><th className="p-1.5 text-left">Type</th><th className="p-1.5 text-right">Override</th><th className="p-1.5 text-left">Note</th><th className="p-1.5"></th></tr>
              </thead>
              <tbody>
                {holidays.map((h) => (
                  <tr key={h.date} className="border-t border-zinc-100">
                    <td className="p-1.5">{fmtD(h.date)}</td>
                    <td className="p-1.5 uppercase text-[10px] font-bold">{h.kind}</td>
                    <td className="p-1.5 text-right">{h.capacity_override ?? '—'}</td>
                    <td className="p-1.5">{h.note}</td>
                    <td className="p-1.5 text-right"><button onClick={() => delHoliday(h.date)} className="text-red-500 hover:text-red-700"><Trash size={14} /></button></td>
                  </tr>
                ))}
                {holidays.length === 0 && <tr><td colSpan="5" className="p-3 text-center text-zinc-400">No holidays configured (Sundays are always non-working)</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
